import styled from "styled-components";

/**
 * Implements the button design from https://move.invisionapp.com/d/main/#/console/16589686/343995525/inspect
 *
 * Usage:
 *   <Button primary>Primary</Button>
 *   <Button destructive>Destructive</Button>
 *   <Button secondary>Secondary</Button>
 *   <Button neutral>Neutral</Button>
 *   <Button primary disabled={true}>Primary, Disabled</Button>
 */

function isStyledPrimary(props) {
  return props.primary || props.destructive || props.positive
}

function isStyledSecondary(props) {
  return props.neutral || props.secondary
}

function calcCursor(props) {
  return (props.disabled === true) ? 'default' : 'pointer'
}

function calcTextColor(props) {
  if (props.disabled === true) {
    return props.oldColorPalette === true ? '#FFFFFF' : '#B4B3B3'
  }

  if (isStyledPrimary(props)) {
    return '#FFFFFF'
  } else if (props.neutral) {
    return '#1C1C1C'
  } else if (props.secondary) {
    return '#22A7FF'
  }

  return '#FFF'
}

function calcBackgroundColor(props) {
  if (props.disabled === true) {
    if (isStyledPrimary(props)) {
      return props.oldColorPalette === true? '#BBBBBB' : '#D8D8D8'
    } else if (isStyledSecondary(props)) {
      return 'transparent'
    }
  }

  if (props.primary) {
    return props.oldColorPalette === true ? '#0088DD' : '#22A7FF'
  } else if(props.positive) {
    return '#59C48E'
  } else if (props.destructive) {
    return '#FF5A5A'
  } else if (isStyledSecondary(props)) {
    return 'transparent'
  }

  return '#FFF'
}

function calcBackgroundColor_HoverState(props) {

  if (props.disabled === true) {
    if (isStyledPrimary(props)) {
      return props.oldColorPalette === true? '#BBBBBB' : '#D8D8D8'
    } else if (isStyledSecondary(props)) {
      return 'transparent'
    }
  }

  if (props.primary) {
    return props.oldColorPalette === true ? '#0088DD' : '#22A7FF'
  } else if(props.positive) {
    return props.oldColorPalette === true ? '#59C48E' : '#4FB07F'
  } else if (props.destructive) {
    return '#E55050'
  } else if (isStyledSecondary(props)) {
    return 'transparent'
  }

  return '#FFF'
}

function calcBackgroundColor_ActiveState(props) {

  if (props.disabled === true) {
    if (isStyledPrimary(props)) {
      return '#D8D8D8'
    } else if (isStyledSecondary(props)) {
      return 'transparent'
    }
  }

  if (props.primary) {
    return props.oldColorPalette === true ? '#0088DD' : '#0588DD'
  } else if(props.positive) {
    return props.oldColorPalette === true ? '#59C48E' : '#4FB07F'
  } else if (props.destructive) {
    return '#E55050'
  } else if (isStyledSecondary(props)) {
    return 'transparent'
  }

  return '#FFF'
}

function calcBorderColor(props) {

  if (props.disabled === true) {
    if (isStyledPrimary(props)) {
      return props.oldColorPalette === true ? '#BBBBBB' : '#D8D8D8'
    } else if (isStyledSecondary(props)) {
      return '#B4B3B3'
    }
  }

  if (props.primary) {
    return props.oldColorPalette === true ? '#0088DD' : '#22A7FF'
  } else if(props.positive) {
    return props.oldColorPalette === true ? '#59C48E' : '#FFF'
  } else if (props.destructive) {
    return '#FF5A5A'
  } else if (props.neutral) {
    return '#1C1C1C'
  } else if (props.secondary) {
    return '#22A7FF'
  }

  return '#FFF'
}

function calcBorderColor_HoverState(props) {
  if (props.disabled !== true && isStyledPrimary(props)) {
    return calcBackgroundColor_HoverState(props)
  } else {
    calcBorderColor(props)
  }
}

function calcBorderColor_ActiveState(props) {
  if (props.disabled !== true && isStyledPrimary(props)) {
    return calcBackgroundColor_HoverState(props)
  } else {
    calcBorderColor(props)
  }
}

const borderWidthPx = 1
const hoverBorderSizeDelta = 0.75

function calcPadding(props) {
    return `${props.paddingTopBottom || 6}px ${props.paddingLeftRight || 20}px !important`
}

function calcPaddingHover(props) {
  return `${(props.paddingTopBottom || 6) - hoverBorderSizeDelta}px ${(props.paddingLeftRight || 20) - hoverBorderSizeDelta}px !important`
}

export default styled.button`
  font-family: "Circular", sans-serif;
  font-weight: ${props => props.fontWeight || 'bold'}
  font-size: ${props => props.fontSize || props.largerFont === true ? '16px' : '13px'}
  line-height: ${props => props.largerFont === true ? '21px' : '16px'}
  letter-spacing: ${props => props.largerFont === true ? '0.57' : '0'}

  display: block;
  outline: none;
  cursor: ${props => calcCursor(props)};
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
  border-radius: ${props => props.borderRadius || '2px'}
  box-shadow: none;
  color: ${props => calcTextColor(props)};
  background-color: ${props => calcBackgroundColor(props)};
  border: ${props => calcBorderColor(props)} solid ${borderWidthPx}px;
  pointer-events: ${props => props.disabled ? 'none' : 'auto'}
  padding: ${props => calcPadding(props)};
  margin-right: 12px;
  
  &:hover {
    background-color: ${props => calcBackgroundColor_HoverState(props)};
    border-color: ${props => calcBorderColor_HoverState(props)};
    border-width: ${borderWidthPx + hoverBorderSizeDelta}px;
    padding: ${props => calcPaddingHover(props)};
  }
  
  &:active {
    background-color: ${props => calcBackgroundColor_ActiveState(props)};
    border-color: ${props => calcBorderColor_ActiveState(props)};
    border-width: ${borderWidthPx}px;
    padding: ${props => calcPadding(props)};
  }
  
  &:last-of-type {
    margin-right: 0;
  }
`
